'use client';

import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { Button, Paper } from '@mui/material';
import { Tabs, Spinner } from '@repo/components';
import { guardedClient as guardedRequest } from '@repo/shared/helpers';
import { EventEmitter } from '@repo/shared/helpers';
import { Settings, CustomerThemeSettings, LanguagesSettings, GeneralSettings } from '@repo/shared/domain';
import BrandingTab from './Tabs/BrandingTab';
import LanguagesTab from './Tabs/LanguagesTab';
import GeneralTab from './Tabs/GeneralTab';
import { getMultipleSettings } from 'src/api/admin/settings';
import { getAllGroupTypes } from 'src/api/admin/types';

export const Route = createFileRoute('/_admin/config/general/')({
	component: GeneralConfigPage,
});

function GeneralConfigPage() {
	const queryClient = useQueryClient();
	const [activeTab, setActiveTab] = useState(0);
	const [brandingData, setBrandingData] = useState<CustomerThemeSettings['settings']>({});
	const [languagesData, setLanguagesData] = useState<LanguagesSettings['settings']>({});
	const [generalData, setGeneralData] = useState<GeneralSettings['settings']>({});
	const [hasChanged, setHasChanged] = useState(false);
	const [initialized, setInitialized] = useState(false);

	useBreadcrumbLevel(0, 'Configuration');
	useBreadcrumbLevel(1, 'General');

	const { data: settingsData, isLoading } = useQuery({
		queryKey: ['general-settings'],
		queryFn: async () => {
			const results = await getMultipleSettings([Settings.CustomerTheme, Settings.Languages, Settings.General]);
			return results;
		},
	});

	const { data: groupTypes, isLoading: groupTypesLoading } = useQuery({
		queryKey: ['group-types-all'],
		queryFn: async () => {
			const body = await getAllGroupTypes('noPagination=true');
			return body.data;
		},
	});

	// Initialize state when data is loaded
	if (settingsData && !initialized) {
		const brandingSettings = (settingsData[Settings.CustomerTheme] as CustomerThemeSettings)?.settings || {};
		const languagesSettings = (settingsData[Settings.Languages] as LanguagesSettings)?.settings || {};
		const generalSettings = (settingsData[Settings.General] as GeneralSettings).settings || {};
		setBrandingData(brandingSettings);
		setLanguagesData(languagesSettings);
		setGeneralData(generalSettings);
		setInitialized(true);
	}

	const saveBrandingChanges = async () => {
		try {
			await guardedRequest.put('/api/settings/customer-theme', brandingData);
			setHasChanged(false);
			queryClient.invalidateQueries({ queryKey: ['general-settings'] });
			EventEmitter.emit('toast', { type: 'success', message: 'Branding settings updated!' });
		} catch (_err) {
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to update branding settings.' });
		}
	};

	const saveLanguagesChanges = async () => {
		try {
			if (!languagesData.defaultLanguage) {
				EventEmitter.emit('toast', { type: 'error', message: 'Please select a default language.' });
				return;
			}
			await guardedRequest.put('/api/settings/languages', languagesData);
			setHasChanged(false);
			queryClient.invalidateQueries({ queryKey: ['general-settings'] });
			queryClient.invalidateQueries({ queryKey: ['settings', Settings.Languages] });
			EventEmitter.emit('toast', { type: 'success', message: 'Language settings updated!' });
		} catch (_err) {
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to update language settings.' });
		}
	};

	const saveGeneralChanges = async () => {
		const value = generalData?.expiryNotificationDays;
		if (typeof value === 'number' && (value < 14 || value > 60)) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Expiry Warning Interval must be between 14 and 60 days.',
			});
			return;
		}
		try {
			await guardedRequest.put('/api/settings/general', generalData);
			setHasChanged(false);
			queryClient.invalidateQueries({ queryKey: ['general-settings'] });
			EventEmitter.emit('toast', { type: 'success', message: 'General settings updated!' });
		} catch (_err) {
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to update general settings.' });
		}
	};

	const handleBrandingChange = (data: CustomerThemeSettings['settings']) => {
		setHasChanged(true);
		setBrandingData(data);
	};

	const handleLanguagesChange = (data: LanguagesSettings['settings']) => {
		setHasChanged(true);
		data.enabledLanguages = data.enabledLanguages.filter((language) => language !== data.defaultLanguage);
		setLanguagesData({ ...data });
	};

	const handleGeneralChange = (data: GeneralSettings['settings']) => {
		setHasChanged(true);
		setGeneralData(data);
	};

	if (isLoading || groupTypesLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	const tabs = [
		{
			label: 'General',
			element: <GeneralTab data={generalData} onChange={handleGeneralChange} groupTypes={groupTypes} />,
		},
		{
			label: 'Branding',
			element: <BrandingTab data={brandingData} onChange={handleBrandingChange} />,
		},
		{
			label: 'Languages',
			element: <LanguagesTab data={languagesData} onChange={handleLanguagesChange} />,
		},
	];

	const getSaveButton = () => {
		const saveFunctions = [saveGeneralChanges, saveBrandingChanges, saveLanguagesChanges];
		const saveFunction = saveFunctions[activeTab];

		if (!saveFunction) return null;

		return (
			<Button disabled={!hasChanged} size="small" variant="outlined" onClick={saveFunction}>
				Save Changes
			</Button>
		);
	};

	return (
		<div className="min-h-full w-full p-4">
			<Paper variant="outlined">
				<Tabs value={activeTab} onChange={(value) => setActiveTab(value)} actions={getSaveButton()}>
					<Tabs.TabList>
						{tabs.map((tab, index) => (
							<Tabs.Tab key={tab.label} value={index} label={tab.label} />
						))}
					</Tabs.TabList>
					{tabs.map((tab, index) => (
						<Tabs.TabPanel key={tab.label} value={index} selectedValue={activeTab}>
							<div>{tab.element}</div>
						</Tabs.TabPanel>
					))}
				</Tabs>
			</Paper>
		</div>
	);
}
